// Sign-in endpoints (plan 11.4) and the "who am I" query that every screen reads its permissions from.
import { ReturnPath, orderGates, type Gate } from '@aiax-hub/contracts';
// Shapes are the server contract (packages/contracts api/auth.ts).
import { useQuery } from '@tanstack/react-query';
import { ApiError, apiFetch } from './client';
import {
  AuthMeSchema,
  AuthOptionsSchema,
  LoginResultSchema,
  type AuthMe,
  type AuthOptions,
  type LoginRequest,
  type LoginResult,
  type MfaRequest,
  type PasswordChangeRequest,
  type SetupRequest,
} from './types';

export const AUTH = {
  options: '/api/v1/auth/options',
  login: '/api/v1/auth/login',
  mfa: '/api/v1/auth/mfa',
  password: '/api/v1/auth/password',
  setup: '/api/v1/auth/setup',
  logout: '/api/v1/auth/logout',
  me: '/api/v1/auth/me',
  reauth: '/api/v1/auth/reauth',
  /** Browser navigation (not fetch): the server redirects to Google and back (PKCE, state, nonce). */
  googleStart: '/api/v1/auth/google/start',
} as const;

export const authKeys = {
  me: ['auth', 'me'] as const,
  options: ['auth', 'options'] as const,
};

/** Set when a 401 ended the session, so the sign-in screen can say "로그인이 끝났어요" once. */
export const authFlags = { expired: false };

/** Signed-in person, or null when not signed in (401 is an answer here, not an error). */
export async function fetchMe(signal?: AbortSignal): Promise<AuthMe | null> {
  try {
    return await apiFetch(AUTH.me, { schema: AuthMeSchema, allow401: true, signal });
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

export function useMe() {
  return useQuery({ queryKey: authKeys.me, queryFn: ({ signal }) => fetchMe(signal), staleTime: 60_000 });
}

/**
 * First-sign-in gates in the plan 11.4.4 order, regardless of the response order.
 */
export function pendingGates(me: AuthMe | null | undefined): Gate[] {
  if (!me) return [];
  return orderGates(me.gates ?? []);
}

export const fetchAuthOptions = (signal?: AbortSignal): Promise<AuthOptions> => apiFetch(AUTH.options, { schema: AuthOptionsSchema, allow401: true, signal });
export function useAuthOptions() {
  return useQuery({ queryKey: authKeys.options, queryFn: ({ signal }) => fetchAuthOptions(signal), staleTime: 5 * 60_000 });
}

// The calls below answer 401/423/429 for wrong credentials or a lock — the sign-in screen handles them itself.
export const login = (body: LoginRequest): Promise<LoginResult> => apiFetch(AUTH.login, { method: 'POST', body, schema: LoginResultSchema, allow401: true });
export const submitMfa = (body: MfaRequest): Promise<LoginResult> => apiFetch(AUTH.mfa, { method: 'POST', body, schema: LoginResultSchema, allow401: true });
/** PasswordChangeResponse / SetupResponse are `{ status: 'ok', … }` — read as a login status so the screen continues the same way. */
export const changePassword = (body: PasswordChangeRequest): Promise<LoginResult | undefined> =>
  apiFetch<LoginResult | undefined>(AUTH.password, { method: 'POST', body, allow401: true }).then((r) => (r && typeof r === 'object' && 'status' in r ? LoginResultSchema.parse(r) : undefined));
export const setupFirstAdmin = (body: SetupRequest): Promise<LoginResult | undefined> =>
  apiFetch<LoginResult | undefined>(AUTH.setup, { method: 'POST', body, allow401: true }).then((r) => (r && typeof r === 'object' && 'status' in r ? LoginResultSchema.parse(r) : undefined));
export const logout = (): Promise<void> => apiFetch<void>(AUTH.logout, { method: 'POST', allow401: true });

/** A same-site browser path, shared with the server contract. */
export function returnToOf(path: string | null | undefined): string | null {
  const parsed = ReturnPath.safeParse(path);
  return parsed.success ? parsed.data : null;
}

/** Start Google sign-in (full page navigation). `next` must be a same-site relative path; it comes back as `returnTo`. */
export function googleStartUrl(next?: string | null): string {
  const returnTo = returnToOf(next);
  return returnTo ? `${AUTH.googleStart}?returnTo=${encodeURIComponent(returnTo)}` : AUTH.googleStart;
}

/** Google step-up before opening someone else's text: prompt=login, max_age from the policy; back to `path` afterwards. */
export function googleReauthUrl(path: string): string {
  const returnTo = returnToOf(path);
  return `${AUTH.googleStart}?purpose=reauth${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`;
}
